/**
 * Persistent auth-token storage that survives Android app updates.
 *
 * WHY THIS EXISTS
 * ---------------
 * On the Capacitor Android build the app runs inside a WebView pointed at
 * `https://allurexp.com`. Every `localStorage.setItem(...)` call ends up in
 * the WebView's per-origin storage. Android periodically wipes that storage
 * when the APK is upgraded, when the user clears app data, or when the OS
 * reclaims cache — which was making returning users sign in from scratch
 * on every app update (reported by the user in Feb 2026).
 *
 * The fix is to mirror the token + user object into Capacitor's native
 * `@capacitor/preferences` plugin, which is backed by Android's
 * `SharedPreferences` — the same store the OS uses for the app's
 * settings. That store survives:
 *   • APK updates
 *   • WebView cache clears
 *   • OS storage-pressure evictions
 *
 * ONLY app uninstall or "Clear Data" from Android settings wipes it,
 * which is the correct trigger for a re-sign-in.
 *
 * STRATEGY
 * --------
 * We write to BOTH storages on every setToken() call. On boot (index.js)
 * we call `hydrateAuthFromNative()` which, if `localStorage.token` is
 * empty but Preferences has a token, copies it back into localStorage.
 * That way the 30+ files across the codebase that read via
 * `localStorage.getItem('token')` keep working unchanged.
 *
 * On web / non-Capacitor builds Preferences falls back to a wrapped
 * localStorage implementation, so no branching is needed.
 */
import { Preferences } from '@capacitor/preferences';

// Keeping the localStorage keys identical to the historical ones so every
// existing `localStorage.getItem('token')` call keeps working without a
// refactor. The Preferences mirror uses the same keys.
const TOKEN_KEY = 'token';
const USER_KEY = 'user';
const PASSKEY_FLAG_KEY = 'allure_has_passkey';
// Set right after login/register/OAuth completes. UnlockPage checks this
// on mount and, if it sees a fresh timestamp, bypasses the biometric
// prompt entirely — the user JUST authenticated, don't double-gate them.
const FRESH_LOGIN_KEY = 'allure_fresh_login_at';
// Any user preference we want to survive app updates too, e.g. the
// biometric-nudge cooldown, gets mirrored via the same helpers.
const MIRRORED_LOCAL_KEYS = [
  PASSKEY_FLAG_KEY,
  'allure_passkey_prompt_dismissed_at',
  'lastActiveRole',
  'lastActiveRolePath',
];

const _lsGet = (key) => {
  try { return window.localStorage.getItem(key); } catch { return null; }
};
const _lsSet = (key, value) => {
  try {
    if (value == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch { /* private mode / disabled */ }
};

const _prefGet = async (key) => {
  try {
    const { value } = await Preferences.get({ key });
    return value ?? null;
  } catch {
    return null;
  }
};
const _prefSet = async (key, value) => {
  try {
    if (value == null) await Preferences.remove({ key });
    else await Preferences.set({ key, value });
  } catch { /* Preferences isn't available in this context (SSR etc.) */ }
};

/**
 * Called once, VERY early in index.js — before React renders. If the
 * WebView's localStorage was wiped (fresh install / app update) but the
 * native Preferences store still has our token, we copy it back into
 * localStorage so the rest of the app boots as a signed-in user without
 * a single line of downstream code needing to change.
 *
 * Also installs a small monkey-patch on `localStorage.setItem` /
 * `removeItem` that mirrors changes to the auth keys OUT to native
 * Preferences. That way the 8+ pages across the codebase that log the
 * user out with `localStorage.removeItem('token')` automatically clean
 * the native mirror too — no per-file refactor needed.
 *
 * Idempotent — safe to call multiple times, no-ops when nothing to do.
 */
// Key for the session-alive timestamp stored in native Preferences.
// When Android kills the WebView renderer during background (aggressive OEM
// battery management), sessionStorage is cleared — making isFreshLaunch()
// return `true` and firing the biometric unlock on every background resume.
// We fix this by storing a "last active" timestamp in Preferences (which
// survives renderer death) and restoring the sessionStorage flag on boot
// if the timestamp is recent enough (within SESSION_RESUME_WINDOW_MS).
const SESSION_ALIVE_TS_KEY = 'allure_session_alive_ts';
const SESSION_ALIVE_SS_KEY = 'allure_session_alive_v1';
// 10-minute window: background resume < 10 min → skip biometric.
// Genuine cold launch (force-quit, app uninstall, restart) will always
// have a stale or missing timestamp → biometric fires as expected.
const SESSION_RESUME_WINDOW_MS = 10 * 60 * 1000;

export const hydrateAuthFromNative = async () => {
  try {
    _installLocalStorageMirror();
    // Token + user are the only two things that MUST hydrate before the
    // React tree mounts. Everything else can hydrate lazily.
    const [nativeToken, nativeUser] = await Promise.all([
      _prefGet(TOKEN_KEY),
      _prefGet(USER_KEY),
    ]);
    if (nativeToken && !_lsGet(TOKEN_KEY)) {
      _lsSet(TOKEN_KEY, nativeToken);
    }
    if (nativeUser && !_lsGet(USER_KEY)) {
      _lsSet(USER_KEY, nativeUser);
    }
    // Mirror non-critical preferences (passkey flag, remembered role,
    // etc.). Best-effort — we don't await these blocking the app boot.
    for (const key of MIRRORED_LOCAL_KEYS) {
      const nativeValue = await _prefGet(key);
      if (nativeValue != null && _lsGet(key) == null) {
        _lsSet(key, nativeValue);
      }
    }
    // Restore the session-alive flag from native Preferences if the renderer
    // was killed recently (background-resume within SESSION_RESUME_WINDOW_MS).
    // This prevents the biometric unlock from firing on every background
    // switch on aggressive-kill Android OEMs (Xiaomi, Samsung, OPPO etc.).
    // Only restores when sessionStorage is EMPTY — i.e. the renderer was killed.
    // If sessionStorage already has the flag, the renderer was alive and no
    // action is needed.
    try {
      const ssAlive = window.sessionStorage.getItem(SESSION_ALIVE_SS_KEY);
      if (!ssAlive) {
        const nativeTs = await _prefGet(SESSION_ALIVE_TS_KEY);
        if (nativeTs) {
          const age = Date.now() - Number(nativeTs);
          if (age < SESSION_RESUME_WINDOW_MS) {
            window.sessionStorage.setItem(SESSION_ALIVE_SS_KEY, '1');
          }
        }
      }
    } catch { /* sessionStorage unavailable — fail safe */ }
  } catch {
    /* fatal boot errors here would strand the user on a blank screen —
       fail silent, they'll just re-sign-in as they do today. */
  }
};

// Keys that should be mirrored native ↔ localStorage in both directions
// when set/removed via the plain localStorage API. Includes the two auth
// keys AND every entry in MIRRORED_LOCAL_KEYS so preferences round-trip
// too.
const AUTO_MIRRORED_KEYS = new Set([TOKEN_KEY, USER_KEY, ...MIRRORED_LOCAL_KEYS]);

// Guard the monkey-patch so hot-reload / repeated hydrate calls don't
// wrap the same methods multiple times (which would fire redundant
// Preferences writes).
let _localStorageMirrorInstalled = false;

const _installLocalStorageMirror = () => {
  if (_localStorageMirrorInstalled) return;
  if (typeof window === 'undefined' || !window.localStorage) return;
  const originalSet = window.localStorage.setItem.bind(window.localStorage);
  const originalRemove = window.localStorage.removeItem.bind(window.localStorage);
  window.localStorage.setItem = (key, value) => {
    originalSet(key, value);
    if (AUTO_MIRRORED_KEYS.has(key)) {
      _prefSet(key, value == null ? null : String(value));
    }
  };
  window.localStorage.removeItem = (key) => {
    originalRemove(key);
    if (AUTO_MIRRORED_KEYS.has(key)) {
      _prefSet(key, null);
    }
  };
  _localStorageMirrorInstalled = true;
};

/** Persist the JWT + user object into BOTH storages. Call this from
 *  every login/register/OAuth/2FA/switch-role success handler.  */
export const persistAuthSession = async (token, user) => {
  const userJson = user ? JSON.stringify(user) : null;
  _lsSet(TOKEN_KEY, token || null);
  _lsSet(USER_KEY, userJson);
  // Mark the moment we authenticated so UnlockPage can skip re-auth.
  _lsSet(FRESH_LOGIN_KEY, String(Date.now()));
  // Fire and forget — the localStorage writes are the authoritative
  // path for the current session; the native mirror is for the NEXT
  // cold launch after an app update.
  _prefSet(TOKEN_KEY, token || null);
  _prefSet(USER_KEY, userJson);
};

/** Persist only the token (used by /auth/renew, which returns a fresh
 *  token but not a whole new user object).  */
export const persistAuthToken = async (token) => {
  _lsSet(TOKEN_KEY, token || null);
  _prefSet(TOKEN_KEY, token || null);
};

/** Update the native session-alive timestamp in Preferences.
 *  Fire-and-forget (async) — safe to call from markSessionAlive(). */
export const touchSessionAliveNative = () => {
  _prefSet(SESSION_ALIVE_TS_KEY, String(Date.now()));
};

/** Wipe every trace of the session from both storages. Called by the
 *  axios 401 interceptor and any explicit logout button. Passkey /
 *  role hints are intentionally kept — they help the NEXT login. */
export const clearAuthSession = async () => {
  _lsSet(TOKEN_KEY, null);
  _lsSet(USER_KEY, null);
  _lsSet(FRESH_LOGIN_KEY, null);
  _prefSet(TOKEN_KEY, null);
  _prefSet(USER_KEY, null);
  _prefSet(FRESH_LOGIN_KEY, null);
  // Also clear the session-alive timestamp so the next cold launch after
  // logout correctly requires biometric (if enrolled).
  _prefSet(SESSION_ALIVE_TS_KEY, null);
};

/** Mirror a mirrored-key change out to the native store. Called from
 *  the passkey enrollment flow (so `allure_has_passkey` survives an
 *  app update) and from `lastActiveRole.js`.  */
export const mirrorLocalKey = (key, value) => {
  if (!MIRRORED_LOCAL_KEYS.includes(key)) return;
  _prefSet(key, value == null ? null : String(value));
};

/** True when the user completed login/register/OAuth within the last
 *  `windowMs` ms. UnlockPage uses this to skip the biometric prompt
 *  right after a fresh sign-in — no need to double-authenticate.  */
export const isFreshLogin = (windowMs = 60 * 1000) => {
  const at = Number(_lsGet(FRESH_LOGIN_KEY) || 0);
  if (!at) return false;
  return Date.now() - at < windowMs;
};

/** Explicitly clear the fresh-login marker. Called once the Unlock or
 *  landing screen has consumed it so subsequent cold launches gate
 *  normally on biometric.  */
export const consumeFreshLogin = () => {
  _lsSet(FRESH_LOGIN_KEY, null);
  _prefSet(FRESH_LOGIN_KEY, null);
};
